// visuals.js
export class ElectionCharts {
  wind: any = window;
  charts: Record<string, any>;

  constructor() {
    this.charts = {
      turnout: { 0: null, 1: null },
      validVotes: { 0: null, 1: null },
      voteDistribution: { 0: null, 1: null },
    };
  }

  /**
   * Helper method to destroy an existing chart.
   */
  private destroyChart(chart: any) {
    if (chart) {
      chart?.destroy();
    }
  }

  /**
   * Creates or updates a chart with the given configuration.
   */
  private createChart(id: string, type: string, data: any, options: any): any {
    const ctx = document.getElementById(id) as HTMLCanvasElement;
    return new this.wind["Chart"](ctx, { type, data, options });
  }

  /**
   * Create a vote distribution chart.
   */
  createVoteDistributionChart(data: number[] | null = [0, 0, 0], id: string, chartIndex: number) {
    this.destroyChart(this.charts.voteDistribution[chartIndex]);

    const chartData = {
      labels: ["NDC", "NPP", "Others"],
      datasets: [
        {
          data,
          labels: ["NDC", "NPP", "Others"],
          backgroundColor: [
            "rgba(53, 121, 55, 1)",
            "rgba(7, 1, 151, 1)",
            "rgba(250, 8, 31, 1)",
          ],
          borderColor: [
            "rgba(53, 121, 55, 1)",
            "rgba(7, 1, 151, 1)",
            "rgba(250, 8, 31, 1)",
          ],
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: "Vote Distribution",
        },
        legend: {
          display: false,
          position: "bottom",
        },
      },
    };

    this.charts.voteDistribution[chartIndex] = this.createChart(
      id,
      "bar",
      chartData,
      chartOptions
    );
  }

  /**
   * Create a turnout bar chart.
   */
  createTurnoutChart(data: number[] | null = [0, 0], id: string, chartIndex: number) {
    this.destroyChart(this.charts.turnout[chartIndex]);

    const chartData = {
      labels: ["Registered", "Voted"],
      datasets: [
        {
          label: ["Registered", "Voted"],

          data,
          backgroundColor: ["rgba(7, 1, 151, 1)", "rgba(75, 192, 19, 1)"],
          borderColor: ["rgba(7, 1, 151, 1)", "rgba(75, 192, 19, 1)"],
          borderWidth: 1,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: "Voter Turnout",
        },
        legend: {
          display: false,
          position: "bottom",
        },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    };

    this.charts.turnout[chartIndex] = this.createChart(id, "bar", chartData, chartOptions);
  }

  /**
   * Create a valid votes donut chart.
   */
  createValidVotesChart(data: number[] | null = [0, 0], id: string, chartIndex: number) {
    this.destroyChart(this.charts.validVotes[chartIndex]);

    const chartData = {
      labels: ["Valid Votes", "Rejected Votes"],
      datasets: [
        {
          data,
          backgroundColor: ["rgba(53, 121, 55, 1)", "rgba(176, 9, 5, 1)"],
          borderColor: ["rgba(53, 121, 55, 1)", "rgba(176, 9, 5, 1)"],
          borderWidth: 1,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: "Vote Validity",
        },
        legend: {
          display: true,
          position: "bottom",
        },
      },
    };

    this.charts.validVotes[chartIndex] = this.createChart(
      id,
      "doughnut",
      chartData,
      chartOptions
    );
  }

  /**
   * Update all charts with new data.
   */
  private updateChartData(chart: any, data: number[]) {
    if (chart) {
      chart.data.datasets[0].data = data;
      chart.update();
    }
  }

  updateCharts(props: Record<string, any>, chartIndex:number) {
    const totalVotes = props.totalvotes ?? 0;
    const rejectedVotes = props.rejectedvotes ?? 0;
    const registeredVoters = props["demographics.eligiblevoters"] ?? 0;

    const totalNpp = props.partyvotes?.npp ?? 0;
    const totalNdc = props.partyvotes?.ndc ?? 0;
    const totalOthers = totalVotes - (totalNdc + totalNpp + rejectedVotes);

    // Update vote distribution chart
    this.updateChartData(this.charts.voteDistribution[chartIndex], [
      totalNdc,
      totalNpp,
      totalOthers,
    ]);

    // Update turnout chart
    this.updateChartData(this.charts.turnout[chartIndex], [registeredVoters, totalVotes]);

    // Update valid votes chart
    this.updateChartData(this.charts.validVotes[chartIndex], [
      totalVotes - rejectedVotes,
      rejectedVotes,
    ]);
  }

  /**
   * Clear all charts by resetting them.
   */
  clearCharts(vdcId: string, tucId: string, vvc: string, chartIndex: number) {
    this.createVoteDistributionChart(null, vdcId, chartIndex);
    this.createTurnoutChart(null, tucId, chartIndex);
    this.createValidVotesChart(null, vvc, chartIndex);
  }
}
